import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Grid,
    CircularProgress,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import AssetService from '../../../../Services/assets.service';
import {
    buildCapitalGainResult,
    computeSelectedEnhancementsTotals,
    hasReplaceableTaxableGain,
    getAvailableRealGainForReplacement,
    getAvailableInflationaryGainForReplacement,
    isReplacementInvalidAfterEnhancements,
    computeAfterReplacementCost,
    getBeforeReplacementCost,
    mapSavedCapitalGainEnhancementToChild,
} from '../../../../Utils/capitalGainCalculations';
import { loadCapitalGainInputs } from '../../../../Utils/capitalGainLoader';
import {
    CAPITAL_GAIN_SUMMARY_STATUS,
    buildCapitalGainSnapshotItem,
    capitalGainSnapshotToInputs,
    getSnapshotAssetId,
} from '../../../../Utils/capitalGainSnapshot';
import { CapitalGainStatusChip } from './CapitalGainSummaryCells';
import CapitalGainReplacementModal from './CapitalGainReplacementModal.jsx';
import {
    CAPITAL_GAIN_RATE_KEYS,
    getCapitalGainTaxRateCaps,
    getDefaultCapitalGainTaxRates,
    getMaxCapitalGainTaxConfig,
    normalizeTaxRate,
} from '../../../../Utils/capitalGainTaxConfig';
import {
    EMPTY_REPLACEMENT_DATA,
    isAssetSold,
    getAssetIdValue,
    sanitizeFileNamePart,
    formatEnhancementAssetLabel,
    getEnhancementAssetId,
    SectionCard,
    EnhancementsPickerDialog,
    CapitalGainAssetDetailsSection,
    CapitalGainIndexCalculationSection,
    CapitalGainTaxCalculationSection,
} from './CapitalGainModalComponents';
import AssetModalShell from '../AssetModalShell/AssetModalShell';
import shell from '../AssetModalShell/AssetModalShell.module.scss';

const CapitalGainCalculationModal = ({
    open = false,
    onClose = () => {},
    asset = null,
    caseId,
    clientName,
    caseType,
    direction,
    token,
    onSnapshotChange = () => {},
    onSellAmountChange = () => {},
}) => {
    const { t } = useTranslation();
    const isRtl = direction === 'rtl';

    const [loading, setLoading] = useState(false);
    const [cpiError, setCpiError] = useState(null);
    const [basis, setBasis] = useState(null);
    const [cpi, setCpi] = useState(null);

    const [salePriceInput, setSalePriceInput] = useState('');
    const [saleExpenses, setSaleExpenses] = useState('0');
    const [rates, setRates] = useState({});
    const [advancePaid, setAdvancePaid] = useState('0');
    const [isDownloadingCapitalGainPdf, setIsDownloadingCapitalGainPdf] = useState(false);
    const [enhancementsOpen, setEnhancementsOpen] = useState(false);
    const [relatedEnhancementAssets, setRelatedEnhancementAssets] = useState([]);
    const [loadingEnhancementCandidates, setLoadingEnhancementCandidates] = useState(false);
    const [savingEnhancements, setSavingEnhancements] = useState(false);
    const [selectedEnhancementAssets, setSelectedEnhancementAssets] = useState([]);
    const [savedEnhancementIds, setSavedEnhancementIds] = useState(new Set());
    const [savedEnhancementAssets, setSavedEnhancementAssets] = useState([]);
    const [replacementOpen, setReplacementOpen] = useState(false);
    const [replacementData, setReplacementData] = useState(EMPTY_REPLACEMENT_DATA);
    const [soldAsset, setSoldAsset] = useState(null);
    const [soldAssetLoading, setSoldAssetLoading] = useState(false);
    const [soldAssetError, setSoldAssetError] = useState(null);
    const [snapshot, setSnapshot] = useState(null);
    const [savingSnapshot, setSavingSnapshot] = useState(false);
    const [savingSellAmount, setSavingSellAmount] = useState(false);

    const effectiveSoldAsset = soldAsset || asset;
    const effectiveAssetId = getAssetIdValue(effectiveSoldAsset);
    const isSold = isAssetSold(effectiveSoldAsset);

    const mapSavedEnhancementsToSelectedAssets = (saved = []) =>
        saved.map((row) => mapSavedCapitalGainEnhancementToChild(row));

    const applySavedEnhancementsState = (saved = []) => {
        const mapped = mapSavedEnhancementsToSelectedAssets(saved);
        const savedIds = new Set(mapped.map(getEnhancementAssetId).filter(Boolean));
        setSelectedEnhancementAssets(mapped);
        setSavedEnhancementAssets(mapped);
        setSavedEnhancementIds(savedIds);
    };

    const inputFieldSx = (width) => ({
        width,
        maxWidth: width,
        '& .MuiInputBase-root': {
            height: 28,
        },
        '& .MuiInputBase-input': {
            py: 0,
            px: 0.75,
            fontSize: 13,
            textAlign: direction === 'rtl' ? 'left' : 'right',
            direction: 'ltr',
        },
    });

    const tableInputSx = (width) => ({
        width,
        maxWidth: width,
        mx: 'auto',
        '& .MuiInputBase-root': {
            height: 26,
            backgroundColor: '#fff',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#9e9e9e',
        },
        '& .MuiInputBase-input': {
            py: 0,
            px: 0.5,
            fontSize: 13,
            textAlign: 'center',
            direction: 'ltr',
        },
    });

    useEffect(() => {
        if (!open || !caseId || !token || !asset?.id) return;

        let cancelled = false;

        const fetchSoldAsset = async () => {
            try {
                setSoldAssetLoading(true);
                setSoldAssetError(null);
                setSoldAsset(null);

                const response = await AssetService.GetAssetById(caseId, asset.id, token);
                if (cancelled) return;

                const fetchedAsset = response?.data ?? null;
                if (!response?.success || !fetchedAsset) {
                    throw new Error(response?.error || 'Sold asset was not found');
                }

                setSoldAsset(fetchedAsset);
            } catch (error) {
                console.error('Failed to fetch sold asset for capital gain modal:', error);
                if (!cancelled) {
                    setSoldAssetError(error);
                    setSoldAsset(asset);
                }
            } finally {
                if (!cancelled) setSoldAssetLoading(false);
            }
        };

        fetchSoldAsset();

        return () => {
            cancelled = true;
        };
    }, [open, caseId, asset?.id, token]);

    useEffect(() => {
        if (!open || !effectiveAssetId || !caseId || !token || soldAssetLoading) return;
        if (!isSold) return;

        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setCpiError(null);
            setBasis(null);
            setCpi(null);

            try {
                const inputs = await loadCapitalGainInputs({
                    caseId,
                    assetId: effectiveAssetId,
                    token,
                    fallbackAsset: effectiveSoldAsset,
                });
                if (cancelled) return;

                const saleYearOfAsset = inputs.basis.sellDate
                    ? new Date(inputs.basis.sellDate).getFullYear()
                    : null;
                let savedSnapshot = null;
                if (saleYearOfAsset) {
                    try {
                        const snapResp = await AssetService.GetCapitalGainSnapshots(caseId, saleYearOfAsset, token);
                        savedSnapshot =
                            (snapResp?.data || []).find((row) => getSnapshotAssetId(row) === effectiveAssetId) ||
                            null;
                    } catch (snapErr) {
                        console.error('Failed to load capital gain snapshot:', snapErr);
                    }
                }
                if (cancelled) return;

                setSnapshot(savedSnapshot);
                setBasis(inputs.basis);
                setRelatedEnhancementAssets([]);
                setReplacementData(inputs.replacements);
                applySavedEnhancementsState(inputs.savedEnhancements);
                setCpi(inputs.cpi);
                if (inputs.cpiError) setCpiError(t(inputs.cpiError) || inputs.cpiError);

                const sellAmount = inputs.basis.sellAmount ?? effectiveSoldAsset?.sellAmount ?? effectiveSoldAsset?.SellAmount;
                setSalePriceInput(sellAmount != null && sellAmount !== '' ? String(sellAmount) : '');
            } catch (err) {
                if (!cancelled) {
                    const message = t(err?.message) || err?.message || t('FailedToLoadCapitalGainData');
                    toast.error(message);
                    setCpiError(message);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [open, effectiveAssetId, caseId, token, soldAssetLoading, isSold, soldAsset, t]);

    const saleDate =
        basis?.sellDate ||
        effectiveSoldAsset?.sellDate ||
        effectiveSoldAsset?.SellDate;
    const saleYear = saleDate ? new Date(saleDate).getFullYear() : null;

    const maxTaxConfig = useMemo(
        () => getMaxCapitalGainTaxConfig(saleYear, caseType),
        [saleYear, caseType]
    );

    const rateCaps = useMemo(
        () => getCapitalGainTaxRateCaps(saleYear, caseType),
        [saleYear, caseType]
    );

    const defaultRates = useMemo(
        () => getDefaultCapitalGainTaxRates(saleYear, caseType),
        [saleYear, caseType]
    );

    const applyInputs = (savedSnapshot) => {
        const saved = capitalGainSnapshotToInputs(savedSnapshot);
        setRates(
            CAPITAL_GAIN_RATE_KEYS.reduce(
                (acc, key) => ({ ...acc, [key]: String(saved?.rates?.[key] ?? defaultRates[key]) }),
                {}
            )
        );
        setSaleExpenses(String(saved?.saleExpenses ?? 0));
        setAdvancePaid(String(saved?.advancePaid ?? 0));
        if (saved) setSalePriceInput(String(saved.salePrice ?? ''));
    };

    useEffect(() => {
        if (!open) return;
        applyInputs(snapshot);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, defaultRates, snapshot]);

    const handleRateChange = (field, value) => {
        setRates((prev) => ({
            ...prev,
            [field]: value === '' ? '' : String(normalizeTaxRate(value, rateCaps[field])),
        }));
    };

    useEffect(() => {
        if (!open) {
            setEnhancementsOpen(false);
            setSelectedEnhancementAssets([]);
            setSavedEnhancementIds(new Set());
            setSavedEnhancementAssets([]);
            setRelatedEnhancementAssets([]);
            setReplacementOpen(false);
            setReplacementData(EMPTY_REPLACEMENT_DATA);
            setSoldAsset(null);
            setSoldAssetError(null);
            setSoldAssetLoading(false);
            setSnapshot(null);
        }
    }, [open, asset?.id]);

    const saleDateForEnhancements =
        basis?.sellDate ||
        effectiveSoldAsset?.sellDate ||
        effectiveSoldAsset?.SellDate;
    const parentDepreciationPercentage =
        basis?.parentDepreciationPercentage ?? basis?.ParentDepreciationPercentage ?? 0;

    const { enhancementsBalance, enhancementsAccumulatedDepreciation } = useMemo(
        () =>
            computeSelectedEnhancementsTotals({
                selectedChildren: selectedEnhancementAssets,
                saleDate: saleDateForEnhancements,
                parentDepreciationPercentage,
            }),
        [selectedEnhancementAssets, saleDateForEnhancements, parentDepreciationPercentage]
    );

    const selectedEnhancementIds = useMemo(
        () => new Set(selectedEnhancementAssets.map(getEnhancementAssetId).filter(Boolean)),
        [selectedEnhancementAssets]
    );

    const toggleEnhancementAsset = (child) => {
        const childId = getEnhancementAssetId(child);
        setSelectedEnhancementAssets((prev) => {
            const exists = prev.some((a) => getEnhancementAssetId(a) === childId);
            if (exists) {
                return prev.filter((a) => getEnhancementAssetId(a) !== childId);
            }
            return [...prev, child];
        });
    };

    const confirmRemoveSavedEnhancements = async (removedSavedAssets) => {
        const removedNames = removedSavedAssets
            .map((asset) => formatEnhancementAssetLabel(asset))
            .join(', ');

        const result = await Swal.fire({
            title: 'האם אתה בטוח?',
            text: `האם להסיר את יתרת ההשבחות שנבחרה? ${removedNames}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'כן, הסר ושמור',
            cancelButtonText: 'ביטול',
            reverseButtons: true,
        });

        if (result.isConfirmed) return true;

        setSelectedEnhancementAssets((prev) => {
            const existingIds = new Set(prev.map(getEnhancementAssetId).filter(Boolean));
            const restored = removedSavedAssets.filter((asset) => {
                const id = getEnhancementAssetId(asset);
                return id && !existingIds.has(id);
            });
            return [...prev, ...restored];
        });

        return false;
    };

    const handleRemoveEnhancementAsset = async (asset) => {
        const assetId = getEnhancementAssetId(asset);
        const wasSaved = assetId && savedEnhancementIds.has(assetId);

        if (wasSaved) {
            const result = await Swal.fire({
                title: 'האם אתה בטוח?',
                text: `האם להסיר את יתרת ההשבחות ${formatEnhancementAssetLabel(asset)}?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'כן, הסר',
                cancelButtonText: 'ביטול',
                reverseButtons: true,
            });

            if (!result.isConfirmed) return;
        }

        const nextSelected = selectedEnhancementAssets.filter(
            (item) => getEnhancementAssetId(item) !== assetId
        );
        setSelectedEnhancementAssets(nextSelected);

        if (!wasSaved || !caseId || !effectiveAssetId || !token) return;

        try {
            const remainingIds = nextSelected.map(getEnhancementAssetId).filter(Boolean);
            const resp = await AssetService.SaveCapitalGainEnhancements(
                caseId,
                {
                    soldAssetId: effectiveAssetId,
                    enhancementAssetIds: remainingIds,
                },
                token
            );
            if (!resp?.success) {
                throw new Error(resp?.error || t('FailedToRemoveEnhancements'));
            }

            applySavedEnhancementsState(resp.data || []);
            toast.success(t('EnhancementsRemovedSuccessfully') || 'יתרת ההשבחות הוסרה בהצלחה');
        } catch (error) {
            console.error('Failed to remove saved enhancement:', error);
            toast.error(
                error?.message || t('FailedToRemoveEnhancements') || 'שגיאה בהסרת יתרת ההשבחות'
            );
            setSelectedEnhancementAssets((prev) => {
                const ids = new Set(prev.map(getEnhancementAssetId).filter(Boolean));
                if (ids.has(assetId)) return prev;
                return [...prev, asset];
            });
        }
    };

    const result = useMemo(
        () =>
            buildCapitalGainResult({
                basis,
                cpi,
                enhancements: selectedEnhancementAssets,
                replacements: replacementData,
                inputs: { salePrice: salePriceInput, saleExpenses, rates, advancePaid },
                caseType,
            }),
        [basis, cpi, selectedEnhancementAssets, replacementData, salePriceInput, saleExpenses, rates, advancePaid, caseType]
    );

    const gains = result?.gains ?? null;
    const replacementSummary = result?.replacementSummary ?? null;
    const adjustedGains = result?.adjustedGains ?? null;
    const tax = result?.tax ?? null;

    const canApplyReplacement = useMemo(() => hasReplaceableTaxableGain(gains), [gains]);

    const maxRealGain = useMemo(
        () => getAvailableRealGainForReplacement(gains),
        [gains]
    );

    const maxInflationaryGain = useMemo(
        () => getAvailableInflationaryGainForReplacement(gains),
        [gains]
    );

    const resetReplacementData = async () => {
        setReplacementData(EMPTY_REPLACEMENT_DATA);

        if (!caseId || !effectiveAssetId || !token) return;

        const taxYear = saleDateForEnhancements
            ? new Date(saleDateForEnhancements).getFullYear()
            : new Date().getFullYear();

        const resp = await AssetService.SaveCapitalGainReplacements(
            caseId,
            {
                soldAssetId: effectiveAssetId,
                taxYear,
                rows: [],
            },
            token
        );

        if (!resp?.success) {
            throw new Error(resp?.error || t('FailedToSaveCapitalGainReplacements'));
        }
    };

    const openReplacementPicker = () => {
        setReplacementOpen(true);
    };

    const handleOpenEnhancementsPicker = async () => {
        if (!caseId || !effectiveAssetId || !token) return;
        setLoadingEnhancementCandidates(true);
        try {
            const resp = await AssetService.GetEnhancementCandidates(caseId, effectiveAssetId, token);
            if (!resp?.success) {
                throw new Error(resp?.error || t('FailedToLoadCapitalGainData'));
            }
            setRelatedEnhancementAssets(resp.data || []);
            setEnhancementsOpen(true);
        } catch (err) {
            toast.error(err?.message || t('FailedToLoadCapitalGainData'));
        } finally {
            setLoadingEnhancementCandidates(false);
        }
    };

    const saveSelectedEnhancements = async () => {
        if (!caseId || !effectiveAssetId || !token) return;

        const currentSelectedIds = new Set(
            selectedEnhancementAssets.map(getEnhancementAssetId).filter(Boolean)
        );

        const removedSavedAssets = savedEnhancementAssets.filter((asset) => {
            const id = getEnhancementAssetId(asset);
            return id && savedEnhancementIds.has(id) && !currentSelectedIds.has(id);
        });

        if (removedSavedAssets.length > 0) {
            const confirmed = await confirmRemoveSavedEnhancements(removedSavedAssets);
            if (!confirmed) return;
        }

        const hasSavedReplacement =
            Number(replacementData?.realReplacementAmount || 0) > 0 ||
            Number(replacementData?.inflationaryReplacementAmount || 0) > 0 ||
            (replacementData?.selectedAssets || []).length > 0;

        if (hasSavedReplacement && gains && isReplacementInvalidAfterEnhancements(replacementData, gains)) {
            const result = await Swal.fire({
                title: 'שחלוף נכסים לא תקין',
                text: 'סכום השחלוף אינו תקין לאחר שינוי יתרת ההשבחות. יתרת ההשבחות הקטינה את הרווח החייב ולכן יש לעדכן או להסיר את השחלוף.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'אפס שחלוף ושמור',
                cancelButtonText: 'ביטול',
                reverseButtons: true,
            });

            if (!result.isConfirmed) {
                setSelectedEnhancementAssets([...savedEnhancementAssets]);
                return;
            }

            try {
                await resetReplacementData();
            } catch (error) {
                console.error('Failed to reset replacement after enhancements change:', error);
                toast.error(
                    error?.message ||
                        t('FailedToSaveCapitalGainReplacements') ||
                        'שגיאה באיפוס נתוני השחלוף'
                );
                setSelectedEnhancementAssets([...savedEnhancementAssets]);
                return;
            }
        }

        const enhancementAssetIds = selectedEnhancementAssets
            .map(getEnhancementAssetId)
            .filter(Boolean);

        setSavingEnhancements(true);
        try {
            const resp = await AssetService.SaveCapitalGainEnhancements(
                caseId,
                {
                    soldAssetId: effectiveAssetId,
                    enhancementAssetIds,
                },
                token
            );
            if (!resp?.success) {
                throw new Error(resp?.error || t('FailedToSaveEnhancements'));
            }

            applySavedEnhancementsState(resp.data || []);
            toast.success(t('EnhancementsSavedSuccessfully') || 'יתרת ההשבחות נשמרה בהצלחה');
        } catch (error) {
            console.error('Failed to save capital gain enhancements:', error);
            toast.error(
                error?.message || t('FailedToSaveEnhancements') || 'שגיאה בשמירת יתרת ההשבחות'
            );
        } finally {
            setSavingEnhancements(false);
        }
    };

    const handleReplacementApply = (data) => {
        setReplacementData({
            selectedAssets: data.selectedAssets || [],
            realReplacementAmount: Number(data.realReplacementAmount) || 0,
            inflationaryReplacementAmount: Number(data.inflationaryReplacementAmount) || 0,
        });
    };

    const buildCapitalGainPdfPayload = () => ({
        caseId,
        clientName: clientName || '',
        soldAssetId: effectiveAssetId,
        salePrice: Number(salePriceInput || basis?.sellAmount || 0),
        saleExpenses: Number(saleExpenses || 0),
        enhancementsBalance,
        enhancementsAccumulatedDepreciation,
        asset: {
            assetNumber:
                effectiveSoldAsset?.assetNumber ?? effectiveSoldAsset?.AssetNumber ?? basis?.assetNumber ?? '',
            name: basis?.name ?? effectiveSoldAsset?.name ?? effectiveSoldAsset?.Name ?? '',
            purchaseDate:
                basis?.purchaseDate ??
                effectiveSoldAsset?.purchaseDate ??
                effectiveSoldAsset?.PurchaseDate ??
                null,
            activationDate:
                basis?.activationDate ??
                effectiveSoldAsset?.activationDate ??
                effectiveSoldAsset?.ActivationDate ??
                null,
            sellDate:
                basis?.sellDate ?? effectiveSoldAsset?.sellDate ?? effectiveSoldAsset?.SellDate ?? null,
        },
        basis: basis
            ? {
                  name: basis.name,
                  assetNumber: basis.assetNumber,
                  purchaseDate: basis.purchaseDate,
                  activationDate: basis.activationDate,
                  sellDate: basis.sellDate,
                  originalPrice: Number(basis.originalPrice || 0),
                  accumulatedDepreciationAtSale: Number(basis.accumulatedDepreciationAtSale || 0),
                  remainingOriginalCost: Number(
                      gains?.remainingOriginalCost ?? basis.remainingOriginalCost ?? 0
                  ),
                  sellAmount: Number(salePriceInput || basis.sellAmount || 0),
              }
            : null,
        gains: gains
            ? {
                  totalCapitalGain: Number(gains.totalCapitalGain || 0),
                  remainingOriginalCost: Number(gains.remainingOriginalCost || 0),
                  inflationaryTaxableGain: Number(gains.inflationaryTaxableGain || 0),
                  inflationaryExemptGain: Number(gains.inflationaryExemptGain || 0),
                  realFrom2003: Number(gains.realFrom2003 || 0),
                  real2003To2011: Number(gains.real2003To2011 || 0),
                  realFrom2012: Number(gains.realFrom2012 || 0),
                  realUntil2002: Number(gains.realUntil2002 || 0),
                  realLoss: Number(gains.realLoss || 0),
                  totalAccumulatedDepreciation: Number(gains.totalAccumulatedDepreciation || 0),
              }
            : null,
        indexCalculation: cpi
            ? {
                  purchaseKnownIndexPeriod:
                      cpi.purchaseKnownIndexPeriod ?? cpi.PurchaseKnownIndexPeriod ?? null,
                  saleKnownIndexPeriod: cpi.saleKnownIndexPeriod ?? cpi.SaleKnownIndexPeriod ?? null,
                  selectedBaseDesc: cpi.selectedBaseDesc ?? cpi.SelectedBaseDesc ?? null,
                  purchaseKnownIndex: Number(cpi.purchaseKnownIndex ?? cpi.PurchaseKnownIndex ?? 0),
                  saleKnownIndex: Number(cpi.saleKnownIndex ?? cpi.SaleKnownIndex ?? 0),
                  indexIncreasePercent: Number(cpi.indexIncreasePercent ?? 0),
              }
            : null,
        selectedEnhancementAssets: (selectedEnhancementAssets || []).map((child) => ({
            assetNumber: child?.assetNumber ?? child?.AssetNumber ?? '',
            name: child?.assetName ?? child?.AssetName ?? child?.name ?? child?.Name ?? '',
            purchaseDate: child?.purchaseDate ?? child?.PurchaseDate ?? null,
            originalPrice: Number(child?.originalPrice ?? child?.OriginalPrice ?? 0),
            accumulatedDepreciationAtSale: Number(
                child?.accumulatedDepreciationAtSale ?? child?.AccumulatedDepreciationAtSale ?? 0
            ),
            remainingOriginalCostAtSale: Number(
                child?.remainingOriginalCostAtSale ??
                    child?.RemainingOriginalCostAtSale ??
                    child?.remainingOriginalCost ??
                    child?.RemainingOriginalCost ??
                    0
            ),
        })),
        replacementData: {
            realReplacementAmount: Number(replacementData?.realReplacementAmount || 0),
            inflationaryReplacementAmount: Number(replacementData?.inflationaryReplacementAmount || 0),
            selectedAssets: (replacementData?.selectedAssets || []).map((row) => ({
                assetNumber: row?.assetNumber ?? '',
                assetName: row?.assetName ?? '',
                originalPrice: Number(row?.originalPrice ?? 0),
                realReplacementAmount: Number(row?.realReplacementAmount ?? 0),
                inflationaryReplacementAmount: Number(row?.inflationaryReplacementAmount ?? 0),
                beforeReplacementCost: getBeforeReplacementCost(row),
                afterReplacementCost: computeAfterReplacementCost(row),
            })),
        },
        replacementSummary: replacementSummary
            ? {
                  realReplacementAmount: Number(replacementSummary.realReplacementAmount || 0),
                  inflationaryReplacementAmount: Number(
                      replacementSummary.inflationaryReplacementAmount || 0
                  ),
                  replacementFrom2003: Number(replacementSummary.replacementFrom2003 || 0),
                  replacement2003To2011: Number(replacementSummary.replacement2003To2011 || 0),
                  replacementFrom2012: Number(replacementSummary.replacementFrom2012 || 0),
                  replacementUntil2002: Number(replacementSummary.replacementUntil2002 || 0),
              }
            : null,
        taxRates: {
            inflationaryTaxRate: Number(result?.rates.inflationaryTaxableRate || 0),
            realFrom2003TaxRate: Number(result?.rates.realFrom2012Rate || 0),
            real2003To2011TaxRate: Number(result?.rates.real2003To2011Rate || 0),
            realFrom2012TaxRate: Number(result?.rates.realFrom2012Rate || 0),
            realUntil2002TaxRate: Number(result?.rates.realUntil2002Rate || 0),
        },
        advancePaid: Number(advancePaid || 0),
        adjustedGains: adjustedGains
            ? {
                  adjustedInflationaryTaxableGain: Number(
                      adjustedGains.adjustedInflationaryTaxableGain || 0
                  ),
                  adjustedRealFrom2003: Number(adjustedGains.adjustedRealFrom2003 || 0),
                  adjustedReal2003To2011: Number(adjustedGains.adjustedReal2003To2011 || 0),
                  adjustedRealFrom2012: Number(adjustedGains.adjustedRealFrom2012 || 0),
                  adjustedRealUntil2002: Number(adjustedGains.adjustedRealUntil2002 || 0),
              }
            : null,
        adjustedGainBeforeTax: Number(result?.gainBeforeTax || 0),
        tax: tax
            ? {
                  totalTax: Number(tax.totalTax || 0),
                  taxOnInflationary: Number(tax.taxOnInflationary || 0),
                  taxOnRealFrom2003: Number(tax.taxOnRealFrom2003 || 0),
                  taxOnReal2003To2011: Number(tax.taxOnReal2003To2011 || 0),
                  taxOnRealFrom2012: Number(tax.taxOnRealFrom2012 || 0),
                  taxOnRealUntil2002: Number(tax.taxOnRealUntil2002 || 0),
                  balanceDue: Number(tax.balanceDue || 0),
              }
            : null,
        maxTaxSection: maxTaxConfig?.section ?? null,
        maxTaxRate: maxTaxConfig?.rate ?? null,
    });

    const snapshotYear = snapshot?.taxYear ?? saleYear;

    const isSnapshotOutdated = useMemo(() => {
        if (!snapshot || !result) return false;
        const differs = (a, b) => Math.abs((Number(a) || 0) - (Number(b) || 0)) >= 0.01;
        return (
            differs(snapshot.gainBeforeTax, result.gainBeforeTax) ||
            differs(snapshot.totalTax, result.tax.totalTax) ||
            differs(snapshot.balanceDue, result.tax.balanceDue)
        );
    }, [snapshot, result]);

    /** Writes an edited sale consideration back to the asset's sell amount. */
    const commitSalePrice = async () => {
        if (!basis || !caseId || !effectiveAssetId || !token) return;
        const raw = String(salePriceInput ?? '').trim();
        if (raw === '') return;
        const next = Math.round(Number(raw) * 100) / 100;
        if (Number.isNaN(next) || next < 0) return;
        if (Math.abs(Number(basis.sellAmount ?? 0) - next) < 0.005) return;

        setSavingSellAmount(true);
        try {
            const resp = await AssetService.UpdateCapitalGainSellAmount(caseId, effectiveAssetId, next, token);
            if (!resp?.success) throw new Error(resp?.error);
            const sellAmount = Number(resp.sellAmount ?? next);
            setBasis((prev) => (prev ? { ...prev, sellAmount } : prev));
            toast.success(t('CapitalGainSellAmountUpdated') || 'סכום המכירה עודכן בנכס');
            onSellAmountChange({ assetId: effectiveAssetId, sellAmount });
        } catch (error) {
            console.error('Failed to update the asset sell amount:', error);
            toast.error(t('FailedToUpdateCapitalGainSellAmount') || 'שגיאה בעדכון סכום המכירה בנכס');
        } finally {
            setSavingSellAmount(false);
        }
    };

    const handleSaveSnapshot = async () => {
        if (!result || !caseId || !effectiveAssetId || !token) return;
        await commitSalePrice();
        setSavingSnapshot(true);
        try {
            const resp = await AssetService.SaveCapitalGainSnapshots(
                caseId,
                [buildCapitalGainSnapshotItem(effectiveAssetId, result)],
                token
            );
            if (!resp?.success) throw new Error(resp?.error);
            setSnapshot(resp.data?.[0] ?? null);
            toast.success(t('CapitalGainSnapshotSaved') || 'תמונת המצב נשמרה');
            onSnapshotChange(effectiveAssetId);
        } catch (error) {
            console.error('Failed to save capital gain snapshot:', error);
            toast.error(t('FailedToSaveCapitalGainSnapshot') || 'שגיאה בשמירת תמונת המצב');
        } finally {
            setSavingSnapshot(false);
        }
    };

    const handleResetSnapshot = async () => {
        if (!snapshot || !caseId || !effectiveAssetId || !token) return;
        const confirm = await Swal.fire({
            title: t('CapitalGainResetAssetTitle') || 'איפוס לחישוב',
            text: t('CapitalGainResetAssetText') || 'הנתונים השמורים לנכס יימחקו והערכים יחושבו מחדש.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('CapitalGainResetConfirm') || 'כן, אפס',
            cancelButtonText: t('Cancel') || 'ביטול',
            reverseButtons: true,
        });
        if (!confirm.isConfirmed) return;

        setSavingSnapshot(true);
        try {
            const resp = await AssetService.DeleteCapitalGainSnapshots(
                caseId,
                snapshotYear,
                token,
                effectiveAssetId
            );
            if (!resp?.success) throw new Error(resp?.error);
            const sellAmount = basis?.sellAmount ?? effectiveSoldAsset?.sellAmount ?? effectiveSoldAsset?.SellAmount;
            setSalePriceInput(sellAmount != null && sellAmount !== '' ? String(sellAmount) : '');
            setSnapshot(null);
            toast.success(t('CapitalGainSnapshotsReset') || 'הנתונים אופסו לחישוב');
            onSnapshotChange(effectiveAssetId);
        } catch (error) {
            console.error('Failed to reset capital gain snapshot:', error);
            toast.error(t('FailedToResetCapitalGainSnapshot') || 'שגיאה באיפוס הנתונים');
        } finally {
            setSavingSnapshot(false);
        }
    };

    const handleDownloadCapitalGainPdf = async () => {
        if (!caseId || !effectiveAssetId) return;

        try {
            setIsDownloadingCapitalGainPdf(true);

            const payload = buildCapitalGainPdfPayload();
            const blob = await AssetService.DownloadCapitalGainPdf(
                caseId,
                effectiveAssetId,
                payload,
                token
            );

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            const assetNumber =
                effectiveSoldAsset?.assetNumber || effectiveSoldAsset?.AssetNumber || '';
            const clientNamePart = sanitizeFileNamePart(clientName);
            const fileNameParts = ['טופס_רווח_הון', caseId, clientNamePart, assetNumber].filter(Boolean);
            link.download = `${fileNameParts.join('_')}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success(t('CapitalGainPdfDownloadSuccess') || 'טופס רווח הון הורד בהצלחה');
        } catch (error) {
            console.error('Failed to download capital gain PDF:', error);
            toast.error(t('CapitalGainPdfDownloadError') || 'שגיאה בהורדת טופס רווח הון');
        } finally {
            setIsDownloadingCapitalGainPdf(false);
        }
    };

    if (!open || !asset) return null;

    const assetTitle = `${effectiveSoldAsset?.assetNumber ?? effectiveSoldAsset?.AssetNumber ?? ''} - ${
        basis?.name || effectiveSoldAsset?.name || effectiveSoldAsset?.Name || ''
    }`;

    const canDownload = !soldAssetLoading && !loading && isSold && basis;

    return (
        <>
            <AssetModalShell
                open={open}
                onClose={onClose}
                title={t('CapitalGainFormTitle') || 'רווח הון'}
                subtitle={assetTitle}
                direction={direction}
                size="lg"
                headerActions={canDownload && result ? (
                    <CapitalGainStatusChip
                        t={t}
                        summary={{
                            status: snapshot
                                ? CAPITAL_GAIN_SUMMARY_STATUS.saved
                                : CAPITAL_GAIN_SUMMARY_STATUS.computed,
                            savedAt: snapshot?.updatedAt || snapshot?.createdAt,
                            updatedBy: snapshot?.updatedBy,
                        }}
                    />
                ) : null}
                footerStart={canDownload && result ? (
                    <>
                        <button
                            type="button"
                            className={shell.blueBtn}
                            onClick={handleSaveSnapshot}
                            disabled={savingSnapshot || (snapshot && !isSnapshotOutdated)}
                        >
                            {savingSnapshot ? <CircularProgress size={16} color="inherit" /> : <SaveOutlinedIcon />}
                            {t('CapitalGainSaveSnapshot', { year: saleYear }) || `שמור לשנת ${saleYear}`}
                        </button>
                        {snapshot && (
                            <button
                                type="button"
                                className={shell.ghostBtn}
                                onClick={handleResetSnapshot}
                                disabled={savingSnapshot}
                            >
                                <RestartAltIcon />
                                {t('CapitalGainResetToComputed') || 'אפס לחישוב'}
                            </button>
                        )}
                        {isSnapshotOutdated && (
                            <span className={shell.muted}>
                                {t('CapitalGainSnapshotOutdated') || 'יש שינויים שלא נשמרו'}
                            </span>
                        )}
                    </>
                ) : null}
                footer={(
                    <>
                        <button type="button" className={shell.secondaryBtn} onClick={onClose}>
                            {t('Close') || 'סגור'}
                        </button>
                        {canDownload && (
                            <button
                                type="button"
                                className={shell.primaryBtn}
                                onClick={handleDownloadCapitalGainPdf}
                                disabled={isDownloadingCapitalGainPdf || !gains || !cpi}
                            >
                                {isDownloadingCapitalGainPdf
                                    ? <CircularProgress size={18} color="inherit" />
                                    : <PictureAsPdfIcon />}
                                {isDownloadingCapitalGainPdf
                                    ? t('DownloadingCapitalGainForm') || 'מוריד...'
                                    : t('DownloadCapitalGainForm') || 'הורד טופס רווח הון'}
                            </button>
                        )}
                    </>
                )}
            >
                    {(soldAssetLoading || (loading && isSold)) && (
                        <div className={shell.loading}>
                            <CircularProgress className={shell.spinner} />
                        </div>
                    )}

                    {!soldAssetLoading && !isSold && (
                        <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 14, color: '#666' }}>
                                {t('CapitalGainNotSoldAsset') ||
                                    'לא ניתן לחשב רווח הון לנכס שלא נמכר'}
                            </Typography>
                            {soldAssetError && (
                                <Typography sx={{ fontSize: 12, color: '#999', mt: 1 }}>
                                    {soldAssetError?.message}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {!soldAssetLoading && !loading && isSold && basis && (
                        <Grid container spacing={1.5} sx={{ direction }}>
                            <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: isRtl ? 2 : 1 } }}>
                                <SectionCard title={t('AssetDetails') || 'פרטי נכס'} direction={direction}>
                                    <CapitalGainAssetDetailsSection
                                        direction={direction}
                                        t={t}
                                        basis={basis}
                                        gains={gains}
                                        enhancementsBalance={enhancementsBalance}
                                        enhancementsAccumulatedDepreciation={enhancementsAccumulatedDepreciation}
                                        selectedEnhancementAssets={selectedEnhancementAssets}
                                        onRemoveEnhancementAsset={handleRemoveEnhancementAsset}
                                        onOpenEnhancementsPicker={handleOpenEnhancementsPicker}
                                        loadingEnhancementCandidates={loadingEnhancementCandidates}
                                        saleExpenses={saleExpenses}
                                        onSaleExpensesChange={setSaleExpenses}
                                        salePriceInput={salePriceInput}
                                        onSalePriceInputChange={setSalePriceInput}
                                        onSalePriceCommit={commitSalePrice}
                                        savingSalePrice={savingSellAmount}
                                        inputFieldSx={inputFieldSx}
                                    />
                                </SectionCard>
                            </Grid>

                            <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: isRtl ? 1 : 2 } }}>
                                <SectionCard title={t('IndexCalculation') || 'חישוב מדד'} direction={direction}>
                                    <CapitalGainIndexCalculationSection
                                        direction={direction}
                                        t={t}
                                        isRtl={isRtl}
                                        cpi={cpi}
                                        cpiError={cpiError}
                                        gains={gains}
                                        onOpenReplacementPicker={openReplacementPicker}
                                    />
                                </SectionCard>
                            </Grid>

                            {tax && gains && cpi && (
                                <Grid item xs={12} sx={{ order: 3 }}>
                                    <SectionCard
                                        title={t('CapitalGainFormTitle') || 'רווח הון'}
                                        direction={direction}
                                    >
                                        <CapitalGainTaxCalculationSection
                                            direction={direction}
                                            t={t}
                                            gains={gains}
                                            tax={tax}
                                            replacementSummary={replacementSummary}
                                            adjustedGains={adjustedGains}
                                            rates={rates}
                                            rateCaps={rateCaps}
                                            onRateChange={handleRateChange}
                                            advancePaid={advancePaid}
                                            setAdvancePaid={setAdvancePaid}
                                            tableInputSx={tableInputSx}
                                            isRtl={isRtl}
                                            maxTaxConfig={maxTaxConfig}
                                        />
                                    </SectionCard>
                                </Grid>
                            )}
                        </Grid>
                    )}
            </AssetModalShell>
            <EnhancementsPickerDialog
                open={enhancementsOpen}
                onClose={() => setEnhancementsOpen(false)}
                direction={direction}
                t={t}
                assets={relatedEnhancementAssets}
                selectedIds={selectedEnhancementIds}
                onToggle={toggleEnhancementAsset}
                onSave={saveSelectedEnhancements}
                savingEnhancements={savingEnhancements}
                saleDate={saleDateForEnhancements}
                parentDepreciationPercentage={parentDepreciationPercentage}
            />
            <CapitalGainReplacementModal
                open={replacementOpen}
                onClose={() => setReplacementOpen(false)}
                onApply={handleReplacementApply}
                direction={direction}
                caseId={caseId}
                token={token}
                excludedAssetId={effectiveAssetId}
                soldAssetId={effectiveAssetId}
                initialReplacementRows={replacementData.selectedAssets}
                canApplyReplacement={canApplyReplacement}
                maxRealGain={maxRealGain}
                maxInflationaryGain={maxInflationaryGain}
                gains={gains}
                rates={result?.rates}
                saleDate={saleDateForEnhancements}
                salePrice={result?.salePrice ?? 0}
                parentDepreciationPercentage={parentDepreciationPercentage}
            />
        </>
    );
};

export default CapitalGainCalculationModal;
